import { useState } from "react";

import { Alert, Snackbar, Stack, Typography } from "@mui/material";
import Slide from "@mui/material/Slide";

import { useDisclosure } from "hooks";
import { PageLayout } from "layouts";

import { AddCustomerModal } from "./components/AddCustomerModal/AddCustomerModal";
import { AddSupplierModal } from "./components/AddSupplierModal/AddSupplierModal";
import { CustomerNetworkTable } from "./components/CustomerNetworkTable/CustomerNetworkTable";
import { InboundInvites } from "./components/InboundInvites/InboundInvites";
import { OutboundInvites } from "./components/OutboundInvites/OutboundInvites";
import { SupplierNetworkTable } from "./components/SupplierNetworkTable/SupplierNetworkTable";

import type { InviteTargetType } from "./types";

export function SupplierNetwork() {
  const {
    isOpen: isAddSupplierModalOpen,
    onOpen: openAddSupplierModal,
    onClose: closeAddSupplierModal,
  } = useDisclosure();
  const {
    isOpen: isAddCustomerModalOpen,
    onOpen: openAddCustomerModal,
    onClose: closeAddCustomerModal,
  } = useDisclosure();
  const [refreshOutboundInvites, setRefreshOutboundInvites] = useState(false);
  const [refreshCustomerTable, setRefreshCustomerTable] = useState(false);
  const [refreshSupplierTable, setRefreshSupplierTable] = useState(false);
  const [inviteSuccessMessage, setInviteSuccessMessage] = useState<string>("");
  const {
    isOpen: inviteSnackbarOpened,
    onClose: onInviteSnackbarClose,
    onOpen: openInviteSnackbar,
  } = useDisclosure(false);

  const [inboundMessage, setInboundMessage] = useState<string>("");
  const [inboundSeverity, setInboundSeverity] = useState<"success" | "info">(
    "success",
  );
  const {
    isOpen: inboundSnackbarOpened,
    onClose: onInboundSnackbarClose,
    onOpen: openInboundSnackbar,
  } = useDisclosure(false);

  return (
    <PageLayout>
      <Typography variant="h4" fontWeight="600" mb="1rem">
        My Supplier & Customer Network
      </Typography>
      <Typography variant="subtitle1">
        Simplify your freight management by working with the suppliers you
        already use for your business on Evotrux!
      </Typography>
      <Typography variant="subtitle1" mb="2rem">
        Invite them as suppliers so you can better manage your inbound freight.
      </Typography>

      <Stack spacing={4}>
        <InboundInvites
          onNotification={(
            message: string,
            severity: "success" | "info",
            inviteTargetType: InviteTargetType,
          ) => {
            setInboundMessage(message);
            setInboundSeverity(severity);
            openInboundSnackbar();
            if (severity === "success") {
              // Supplier invite accepted = they become your customer
              // Customer invite accepted = they become your supplier
              if (inviteTargetType === "SUPPLIER") {
                setRefreshCustomerTable(true);
              } else {
                setRefreshSupplierTable(true);
              }
            }
          }}
        />
        <SupplierNetworkTable
          onAddSupplier={openAddSupplierModal}
          refresh={refreshSupplierTable}
          onRefreshComplete={() => setRefreshSupplierTable(false)}
        />
        <CustomerNetworkTable
          refresh={refreshCustomerTable}
          onRefreshComplete={() => setRefreshCustomerTable(false)}
          onAddCustomer={openAddCustomerModal}
        />
        <OutboundInvites
          refresh={refreshOutboundInvites}
          onRefreshComplete={() => setRefreshOutboundInvites(false)}
        />
      </Stack>

      <AddSupplierModal
        open={isAddSupplierModalOpen}
        onClose={closeAddSupplierModal}
        onNotification={(message: string) => {
          closeAddSupplierModal();
          setRefreshOutboundInvites(true);
          setInviteSuccessMessage(message);
          openInviteSnackbar();
        }}
      />

      <AddCustomerModal
        open={isAddCustomerModalOpen}
        onClose={closeAddCustomerModal}
        onNotification={(message: string) => {
          closeAddCustomerModal();
          setRefreshCustomerTable(true);
          setRefreshOutboundInvites(true);
          setInviteSuccessMessage(message);
          openInviteSnackbar();
        }}
      />

      <Snackbar
        open={inviteSnackbarOpened}
        autoHideDuration={4000}
        onClose={onInviteSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={onInviteSnackbarClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {inviteSuccessMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={inboundSnackbarOpened}
        autoHideDuration={4000}
        onClose={onInboundSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={onInboundSnackbarClose}
          severity={inboundSeverity}
          sx={{ width: "100%" }}
        >
          {inboundMessage}
        </Alert>
      </Snackbar>
    </PageLayout>
  );
}
